import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { Link } from "@/lib/router-compat";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useRouteLoaderData } from "@/lib/seo/loader-data";
import type { VerifiedDealerSsr } from "@/lib/seo/verified-dealers-seo";
import { ShieldCheck, ArrowRight, Star, MapPin, Users, Building, Receipt, Scales, Lifebuoy, CarProfile, WarningCircle } from "@phosphor-icons/react";
import { SEOHreflang } from "@/components/seo/SEOHreflang";
import { useCountry } from "@/hooks/useCountry";
import { useTranslationV2 } from "@/hooks/useTranslationV2";
import { COUNTRY_ROUTES } from "@/utils/countryRoutes";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { buildListingSlug } from "@/utils/listingSlug";
import { dealerProfilePath } from '@/utils/dealerUrl';

const formatPrice = (value: number, currency: string) =>
  new Intl.NumberFormat("ro-RO", {
    style: "currency",
    currency: currency || "EUR",
    maximumFractionDigits: 0,
  }).format(value);

interface VerifiedDealer {
  user_id: string;
  email: string;
  listing_count: number;
  subscription_tier: string;
  verified_since: string;
  business_name?: string;
  dealer_id?: string;
  is_active_subscriber: boolean;
  logo_url?: string | null;
  is_verified: boolean;
  canonical_path?: string | null;
}

interface StockItem {
  id: string;
  photo: string;
  make: string | null;
  model: string | null;
  year: number | null;
  price: number | null;
  currency: string;
  location_city: string | null;
}

const i18n = {
  RO: {
    breadcrumb: 'Dealeri verificați',
    heroTitle: 'Dealeri verificați',
    heroSubtitle: 'Firme verificate manual, cu anunțuri active',
    heroDesc: 'Verificăm manual firma dealerului la ANAF și în RISCO: CUI, cod CAEN și statut fiscal. Nu verificăm starea vehiculelor.',
    verified: 'Verificat',
    vehicles: 'vehicule',
    since: 'Din',
    viewOffers: 'Vezi Oferte',
    contactUs: 'Contactează-ne',
    countryFlag: '🇷🇴', countryName: 'România',
  },
  UK: {
    breadcrumb: 'Verified Dealers',
    heroTitle: 'Verified Dealers',
    heroSubtitle: 'The Best Dealers Just a Click Away',
    heroDesc: 'RO Auto Market is the trusted destination for buyers and dealers across Romania, the UK and Germany. We offer listings from verified dealers, total transparency and 0% commission.',
    verified: 'Verified',
    vehicles: 'vehicles',
    since: 'Since',
    viewOffers: 'View Offers',
    contactUs: 'Contact Us',
    countryFlag: '🇬🇧', countryName: 'United Kingdom',
  },
  DE: {
    breadcrumb: 'Verifizierte Händler',
    heroTitle: 'Verifizierte Händler',
    heroSubtitle: 'Die besten Händler nur einen Klick entfernt',
    heroDesc: 'RO Auto Market ist die vertrauenswürdige Plattform für Käufer und Händler in Rumänien, Großbritannien und Deutschland. Angebote von verifizierten Händlern, volle Transparenz und 0% Provision.',
    verified: 'Verifiziert',
    vehicles: 'Fahrzeuge',
    since: 'Seit',
    viewOffers: 'Angebote ansehen',
    contactUs: 'Kontakt',
    countryFlag: '🇩🇪', countryName: 'Deutschland',
  },
};

export default function DealeriVerificati() {
  const { country } = useCountry();
  const { t } = useTranslationV2('dealer');
  const txt = i18n[country] || i18n.RO;
  // Seeded from the route loader so crawlers receive the dealer list in HTML.
  const ssr = useRouteLoaderData<{ country?: string; dealers?: VerifiedDealerSsr[] }>();
  const ssrDealers = ssr?.country === country && Array.isArray(ssr?.dealers) ? ssr.dealers.filter((d) => d.is_verified) : null;
  const [dealers, setDealers] = useState<VerifiedDealer[]>((ssrDealers as VerifiedDealer[] | null) ?? []);
  const [loading, setLoading] = useState(!ssrDealers);
  const [totalListings, setTotalListings] = useState(
    (ssrDealers ?? []).reduce((sum, d) => sum + (d.listing_count || 0), 0),
  );
  const [stockPreview, setStockPreview] = useState<Record<string, StockItem[]>>({});

  useEffect(() => {
    fetchVerifiedDealers();
  }, [country]);

  const fetchVerifiedDealers = async () => {
    try {
      setLoading((prev) => (dealers.length > 0 ? false : prev));
      const { data: dealersData, error: dealersError } = await supabase
        .rpc('get_verified_dealers_public', { p_country_code: country });

      if (dealersError) throw dealersError;

      if (!dealersData || dealersData.length === 0) {
        setDealers([]);
        setLoading(false);
        return;
      }

      const formattedDealers: VerifiedDealer[] = dealersData.map((dealer: any) => ({
        user_id: dealer.user_id,
        email: dealer.email || 'Dealer',
        listing_count: Number(dealer.listing_count) || 0,
        subscription_tier: dealer.subscription_tier || 'Basic',
        verified_since: dealer.verified_since,
        business_name: dealer.business_name || null,
        dealer_id: dealer.dealer_id || null,
        is_active_subscriber: dealer.is_active_subscriber ?? true,
        logo_url: null,
        is_verified: false,
      }));

      // Fetch dealer logos in one shot
      const userIds = formattedDealers.map(d => d.user_id);
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('dealer_profiles')
          .select('user_id, logo_url, verified, risco_verified, subdomain_alias')
          .in('user_id', userIds);
        const logoMap = new Map((profiles || []).map((p: any) => [p.user_id, p]));
        formattedDealers.forEach(d => {
          const profile = logoMap.get(d.user_id);
          d.logo_url = profile?.logo_url || null;
          d.is_verified = profile?.verified === true || profile?.risco_verified === true;
          d.canonical_path = profile ? dealerProfilePath(profile) : null;
        });
      }

      const verifiedDealers = formattedDealers.filter(d => d.is_verified);
      setDealers(verifiedDealers);
      const total = verifiedDealers.reduce((sum: number, d: VerifiedDealer) => sum + d.listing_count, 0);
      setTotalListings(total);

      // Stock photo preview (3 recent vehicles WITH photos, per dealer)
      if (verifiedDealers.length > 0) {
        const map: Record<string, StockItem[]> = {};
        const results = await Promise.all(
          verifiedDealers.map(async ({ user_id: uid }) => {
            const { data } = await supabase
              .from('listings_v2')
              .select('id, user_id, make, model, year, price, currency, photos, location_city, created_at')
              .eq('user_id', uid)
              .eq('status', 'active')
              .not('photos', 'is', null)
              .order('created_at', { ascending: false })
              .limit(12);
            return { uid, rows: data || [] };
          })
        );

        results.forEach(({ uid, rows }) => {
          const list: StockItem[] = [];
          rows.forEach((row: any) => {
            if (list.length >= 3) return;
            const photos = Array.isArray(row.photos) ? row.photos : [];
            const first = typeof photos[0] === 'string' ? photos[0] : photos[0]?.url;
            if (!first || typeof first !== 'string' || !/^https?:\/\//.test(first)) return;
            list.push({
              id: row.id,
              photo: first,
              make: row.make,
              model: row.model,
              year: row.year,
              price: row.price ? Number(row.price) : null,
              currency: row.currency || 'EUR',
              location_city: row.location_city,
            });
          });
          if (list.length === 3) map[uid] = list;
        });
        setStockPreview(map);
      }

    } catch (error) {
      console.error('Error fetching verified dealers:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDealerName = (dealer: VerifiedDealer) => {
    if (dealer.business_name) return dealer.business_name;
      return 'Dealer';
  };

  const dateLang = country === 'UK' ? 'en-GB' : country === 'DE' ? 'de-DE' : 'ro-RO';
  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat(dateLang, { year: 'numeric', month: 'long' }).format(new Date(dateStr));
  };

  const reasons = [
    { icon: Building, title: t('why.r1Title'), desc: t('why.r1Desc') },
    { icon: Receipt, title: t('why.r2Title'), desc: t('why.r2Desc') },
    { icon: Scales, title: t('why.r3Title'), desc: t('why.r3Desc') },
    { icon: Lifebuoy, title: t('why.r4Title'), desc: t('why.r4Desc') },
    { icon: CarProfile, title: t('why.r5Title'), desc: t('why.r5Desc') },
    { icon: WarningCircle, title: t('why.r6Title'), desc: t('why.r6Desc') },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <html lang={country === 'UK' ? 'en' : country === 'DE' ? 'de' : 'ro'} />
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://roautomarket.ro${COUNTRY_ROUTES.dealers(country)}`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t('meta.title')} />
        <meta property="og:description" content={t('meta.description')} />
        <meta property="og:url" content={`https://roautomarket.ro${COUNTRY_ROUTES.dealers(country)}`} />
        <meta property="og:image" content="https://roautomarket.ro/og-roautomarket.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('meta.title')} />
        <meta name="twitter:description" content={t('meta.description')} />
      </Helmet>
      <SEOHreflang canonicalUrl={`https://roautomarket.ro${COUNTRY_ROUTES.dealers(country)}`} />

      {/* ── Hero Section ── */}
      <section className="surface-dark relative overflow-hidden">
        <div className="relative container mx-auto px-4 pt-4 pb-6 md:pt-10 md:pb-12 max-w-6xl">
          <Breadcrumbs items={[{ label: txt.breadcrumb, href: '#' }]} className="mb-6" />

          <h1 className="text-3xl md:text-5xl font-bold italic mb-3 text-foreground">
            {txt.heroTitle}
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl">
            {txt.heroSubtitle}
          </p>
          <p className="mt-4 text-sm md:text-base text-muted-foreground max-w-3xl leading-relaxed">
            {txt.heroDesc}
          </p>
        </div>
      </section>

      {/* ── De ce un dealer verificat ── */}
      <section className="border-y border-border bg-muted/30">
        <div className="container mx-auto px-4 py-10 md:py-14 max-w-5xl">
          <h2 className="text-center text-2xl md:text-3xl font-bold italic mb-3 text-foreground">
            {t('why.title')}
          </h2>
          <p className="text-center text-sm md:text-base text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            {t('why.intro')}
          </p>
          <div className="grid gap-x-10 gap-y-8 md:grid-cols-2">
            {reasons.map((r) => (
              <div key={r.title} className="flex gap-4">
                <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                  <r.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="sans-heading text-[17px] text-foreground mb-1">{r.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{r.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Dealers Grid ── */}
      <section className="container mx-auto px-4 py-12 md:py-16 max-w-6xl">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-10 flex items-center justify-center gap-2 text-foreground">
          <ShieldCheck className="h-6 w-6 text-primary" />
          {t('dealersList.title')} ({loading ? '…' : dealers.length})
        </h2>

        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block h-12 w-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-muted-foreground mt-4">{t('dealersList.loading')}</p>
          </div>
        ) : dealers.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-2xl max-w-lg mx-auto bg-card">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2 text-foreground">{t('dealersList.emptyTitle')}</h3>
            <p className="text-muted-foreground text-sm mb-6 px-4">{t('dealersList.emptyDesc')}</p>
            <Button asChild className="bg-destructive hover:bg-destructive/90 text-destructive-foreground">
              <Link to="/dealeri">{t('dealersList.becomeDealerCta')}</Link>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {dealers.map((dealer) => {
              const name = getDealerName(dealer);
              const stock = stockPreview[dealer.user_id] || [];
      const dealerHref = dealer.canonical_path || `/vehicles?dealer=${dealer.user_id}`;
              return (
                <article
                  key={dealer.user_id}
                  className="group rounded-xl border border-border bg-card shadow-xs overflow-hidden hover:shadow-md hover:border-primary/40 transition-all"
                >
                  <div className="h-28 md:h-32 bg-muted flex items-center justify-center relative overflow-hidden">
                    {dealer.logo_url ? (
                      <img
                        src={dealer.logo_url}
                        alt={`Logo ${name}`}
                        loading="lazy"
                        className="max-h-full max-w-full object-contain p-3"
                      />
                    ) : (
                      <span className="text-5xl md:text-6xl font-black text-muted-foreground select-none">
                        {name.charAt(0)}
                      </span>
                    )}
                    {(
                      <Badge className="absolute top-2 right-2 bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5">
                        <ShieldCheck className="h-3 w-3 mr-0.5" />
                        {txt.verified}
                      </Badge>
                    )}
                  </div>

                  {stock.length > 0 && (
                    <div className="grid grid-cols-3 gap-px bg-border">
                      {stock.map((s) => {
                        const slug = buildListingSlug({
                          id: s.id,
                          make: s.make,
                          model: s.model,
                          year: s.year ?? undefined,
                          location_city: s.location_city,
                        });
                        return (
                          <Link
                            key={s.id}
                            to={`/listing/${slug}`}
                            className="relative block aspect-[4/3] overflow-hidden bg-muted"
                            title={`${s.make ?? ''} ${s.model ?? ''}`.trim()}
                          >
                            <img
                              src={s.photo}
                              alt={`${s.make ?? ''} ${s.model ?? ''} ${s.year ?? ''} — ${name}`.trim()}
                              loading="lazy"
                              onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = 'none'; }}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                            {s.price ? (
                              <span className="absolute bottom-1 left-1 rounded-sm bg-background/85 px-1.5 py-0.5 text-[10px] font-semibold text-foreground">
                                {formatPrice(s.price, s.currency)}
                              </span>
                            ) : null}
                          </Link>
                        );
                      })}
                    </div>
                  )}

                  <div className="p-3 md:p-4 space-y-2">
                    <h3 className="font-bold text-sm md:text-base truncate text-foreground">{name}</h3>
                    {stockPreview[dealer.user_id]?.find((item) => item.location_city)?.location_city && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3 flex-shrink-0" />
                        <span>{stockPreview[dealer.user_id].find((item) => item.location_city)?.location_city}</span>
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      {dealer.listing_count} {country === 'RO' && dealer.listing_count === 1 ? 'vehicul' : txt.vehicles} • {txt.since} {formatDate(dealer.verified_since)}
                    </div>
                    <Button
                      asChild
                      size="sm"
                      className="w-full mt-2 bg-destructive hover:bg-destructive/90 text-destructive-foreground text-xs h-8"
                    >
                      <Link to={dealerHref}>
                        {txt.viewOffers} <ArrowRight className="h-3 w-3 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {/* ── CTA & Contact ── */}
      <section className="container mx-auto px-4 py-12 md:py-16 max-w-5xl">
        <div className="rounded-2xl border border-border bg-card p-8 md:p-12 text-center space-y-6 shadow-xs">
          <h2 className="text-2xl md:text-3xl font-bold italic text-foreground">{t('cta.title')}</h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-sm md:text-base">
            {t('cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="bg-destructive hover:bg-destructive/90 text-destructive-foreground gap-2">
              <Link to="/pricing">
                {t('cta.ctaPlans')} <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/contact">
                {txt.contactUs}
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ── SEO Content ── */}
      <section className="border-t border-border">
        <div className="container mx-auto px-4 py-10 max-w-4xl">
          <h2 className="text-xl font-bold mb-6 text-foreground">{t('seo.whyTitle')}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold mb-1 text-foreground">{t('seo.safetyTitle')}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t('seo.safetyDesc')}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Star className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold mb-1 text-foreground">{t('seo.docsTitle')}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t('seo.docsDesc')}</p>
              </div>
            </div>
          </div>

          <div className="mt-8 p-4 border-l-4 border-primary bg-primary/5 rounded-r-lg">
            <h4 className="font-semibold text-primary mb-1">{t('seo.warningTitle')}</h4>
            <p className="text-xs text-muted-foreground" dangerouslySetInnerHTML={{ __html: t('seo.warningDesc') }} />
          </div>
        </div>
      </section>
    </div>
  );
}
